<script setup lang="ts">
import { reactiveOmit, useVModel } from '@vueuse/core'
import type { AcceptableValue, SelectRootEmits, SelectRootProps } from 'reka-ui'
import { SelectRoot, useForwardProps } from 'reka-ui'
import { computed, nextTick, provide, type Ref, shallowReactive, useAttrs } from 'vue'
import { NATIVE_OPTIONS, type NativeOption } from './nativeOptions'

/**
 * Reka's root draws no element of its own, so whatever attributes a screen gives a Select (a test id, an id)
 * had nowhere to land and were dropped. They land on a hidden native field that mirrors the choice instead:
 * never drawn, never focused and outside the accessibility tree, so it is never what anybody sees or reaches.
 * Reading it reads the Select and setting it sets the Select, so a screen moved off a hand-styled native
 * control onto this one can still be read and driven the way a field is.
 *
 * It is a hidden input rather than a hidden select because a select can only hold a value it already has an
 * option for, and the items in a closed list only register a tick after mounting: a value set straight away
 * would be silently dropped. A value set on it is the item with that value when the list has one, and the
 * string itself otherwise, as Reka's own form select takes it.
 *
 * The mirror carries no `name`: a named Select inside a form is still submitted by Reka's own native select,
 * once. It never wears a class or a style either; it is not a second, restyled control. A `multiple` Select
 * has no single value a field could hold, so it draws no mirror.
 */
defineOptions({ inheritAttrs: false })

const props = defineProps<SelectRootProps>()
const emits = defineEmits<SelectRootEmits>()

const forwarded = useForwardProps(reactiveOmit(props, 'modelValue', 'defaultValue'))

/** Owned here rather than by Reka, so the mirror can set it whether or not the Select is bound with v-model. */
const model = useVModel(props, 'modelValue', emits, {
    defaultValue: props.defaultValue,
    passive: (props.modelValue === undefined) as false,
    deep: true,
}) as Ref<AcceptableValue | AcceptableValue[] | undefined>

const attrs = useAttrs()
const nativeAttrs = computed(() => {
    const { class: _class, style: _style, ...rest } = attrs
    return rest
})

/** Every item the list holds, keyed by the item, read on demand so a changed item is never stale. */
const items = shallowReactive(new Map<symbol, () => NativeOption>())
provide(NATIVE_OPTIONS, {
    register(read) {
        const key = Symbol('select-item')
        items.set(key, read)
        return () => {
            items.delete(key)
        }
    },
})

/** A native field can only hold a string, so each choice is keyed by its value said as one. */
function said(value: unknown): string {
    return value === null || value === undefined ? '' : String(value)
}

/** One entry per distinct value, in the order the list holds them; the first item with a value speaks for it. */
const nativeOptions = computed(() => {
    const byKey = new Map<string, NativeOption & { key: string }>()
    for (const read of items.values()) {
        const option = read()
        const key = said(option.value)
        if (!byKey.has(key)) {
            byKey.set(key, { ...option, key })
        }
    }
    return [...byKey.values()]
})

const nativeValue = computed(() => said(model.value))

/**
 * A value set on the mirror is the item with that value, so a number or an object comes back as itself; one
 * no item has is taken as said. A disabled item cannot be chosen this way any more than from the list.
 *
 * Afterwards the field is put back on the value the Select holds. A choice its owner refuses (or has yet to
 * take) never moves that value, so Vue has nothing to redraw and the field would go on showing a choice that
 * is not in force. One the owner takes redraws the field as any change from outside does.
 */
function fromNative(event: Event): void {
    const field = event.target as HTMLInputElement
    const chosen = field.value
    const option = nativeOptions.value.find(candidate => candidate.key === chosen)
    if (!option?.disabled) {
        model.value = option ? option.value : chosen
    }
    void nextTick(() => {
        field.value = nativeValue.value
    })
}
</script>

<template>
  <SelectRoot
    v-slot="slotProps"
    v-model="model"
    data-slot="select"
    v-bind="forwarded"
    @update:open="emits('update:open', $event)"
  >
    <slot v-bind="slotProps" />
    <input
      v-if="!multiple"
      v-bind="nativeAttrs"
      type="hidden"
      data-slot="select-native"
      :disabled="disabled"
      :value="nativeValue"
      @change="fromNative"
    >
  </SelectRoot>
</template>
